import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import Header from "../../layouts/Header";
import { opsAxiosInstance } from "../../api/axios";

// Same pattern as ChecklistOperations.jsx — hardcoded and visible in the
// bundle, so this route needs to be gated the same way. Worth pulling this
// (and the copy in ChecklistOperations.jsx) into one shared constants file
// so the two never drift out of sync.
const API_KEY = "sYQ8SawD0mdqOtPO18NGs1paiEccyXJpFzsCiz1wXgC";
const API_SECRET = "79SvSQbGhcgCK9GdKTN61xTjiJvelIJVkC7pY7ioPaAOryyqbjy4pir2ckssjoCD";

const ADMIN_HEADERS = {
    headers: {
        "X-API-Key": API_KEY,
        "X-API-Secret": API_SECRET,
    },
};

const PAGE_SIZE = 20;

const CHECKLIST_TYPE_OPTIONS = ["OPENING", "CLOSING", "GENERAL"];
const FREQUENCY_OPTIONS = ["DAILY", "WEEKLY", "BIWEEKLY", "MONTHLY"];

const normalizeData = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    if (Array.isArray(payload?.data?.outlets)) return payload.data.outlets;
    if (Array.isArray(payload?.data?.items)) return payload.data.items;
    return [];
};

// "2026-07-15" -> "15 Jul 2026"
const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// "2026-07-15 10:00:14" -> "15 Jul 2026, 10:00 AM"
const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return "—";
    const d = new Date(dateTimeStr.replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return dateTimeStr;
    return d.toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
};

// Local YYYY-MM-DD (not toISOString(), which UTC-shifts and can roll the
// date back a day — same bug class noted in the Xpanse timezone fix).
const toISODate = (d) => {
    if (!d) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

const StatusPill = ({ status }) => {
    const isSubmitted = status === "SUBMITTED";
    return (
        <span className={`au-status ${isSubmitted ? "is-submitted" : "is-draft"}`}>
            <span className="au-dot" />
            {status === "SUBMITTED" ? "Submitted" : status === "DRAFT" ? "Draft" : status || "—"}
        </span>
    );
};

const AnswerBadge = ({ answer }) => {
    const normalized = (answer || "").toUpperCase();
    const isYes = normalized === "YES";
    const isNo = normalized === "NO";
    const cls = isYes ? "is-yes" : isNo ? "is-no" : "is-other";
    return (
        <span className={`au-answer-badge ${cls}`}>
            <i className={`ri-${isYes ? "checkbox-circle-fill" : isNo ? "close-circle-fill" : "question-line"}`}></i>
            {answer || "—"}
        </span>
    );
};

// Small "7 Yes · 1 No" summary chip used in the list table.
const AnswerCounts = ({ yes, no, total }) => {
    if (total === undefined || total === null) return <span className="au-subtext">—</span>;
    return (
        <div className="au-answer-counts">
            <span className="au-count-yes">{yes ?? 0} Yes</span>
            <span className="au-count-sep">·</span>
            <span className="au-count-no">{no ?? 0} No</span>
        </div>
    );
};

// Shared react-select styling — sized to match the compact table toolbar
// rather than react-select's default (fairly tall) control height.
const selectStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: "31px",
        height: "31px",
        fontSize: "12.5px",
        borderColor: state.isFocused ? "#86b7fe" : "#d5d8dd",
        boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(13,110,253,.15)" : "none",
        "&:hover": { borderColor: "#adb2ba" },
    }),
    valueContainer: (provided) => ({ ...provided, height: "31px", padding: "0 8px" }),
    input: (provided) => ({ ...provided, margin: 0, padding: 0 }),
    indicatorsContainer: (provided) => ({ ...provided, height: "31px" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (provided, state) => ({
        ...provided,
        fontSize: "12.5px",
        backgroundColor: state.isSelected ? "#1c1f23" : state.isFocused ? "#f1f2f4" : "white",
        color: state.isSelected ? "white" : "#212529",
        cursor: "pointer",
    }),
};

const Audit = () => {
    const [loading, setLoading] = useState(false);
    const [submissions, setSubmissions] = useState([]);
    const [outlets, setOutlets] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [categoriesList, setCategoriesList] = useState([]);

    const [outletFilter, setOutletFilter] = useState("all");
    const [checklistTypeFilter, setChecklistTypeFilter] = useState("all");
    const [frequencyFilter, setFrequencyFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [staffFilter, setStaffFilter] = useState("all");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [hasNoAnswersOnly, setHasNoAnswersOnly] = useState(false);

    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ page: 1, per_page: PAGE_SIZE, total: 0, total_pages: 1 });

    const [showDetailModal, setShowDetailModal] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detail, setDetail] = useState(null); // { submission, summary, categories }

    useEffect(() => {
        loadOutlets();
        loadStaff();
        loadCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Any filter change resets to page 1. Debounced so rapid changes (e.g.
    // picking both ends of a date range) don't fire a request per click.
    useEffect(() => {
        const handle = setTimeout(() => {
            setPage(1);
            loadSubmissions(1);
        }, 300);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        outletFilter,
        checklistTypeFilter,
        frequencyFilter,
        statusFilter,
        staffFilter,
        categoryFilter,
        startDate,
        endDate,
        hasNoAnswersOnly,
    ]);

    // Plain page changes (no debounce needed).
    useEffect(() => {
        loadSubmissions(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    const buildParams = (targetPage) => {
        const params = { page: targetPage, per_page: PAGE_SIZE };
        if (outletFilter !== "all") params.outlet_id = outletFilter;
        if (checklistTypeFilter !== "all") params.checklist_type = checklistTypeFilter;
        if (frequencyFilter !== "all") params.frequency = frequencyFilter;
        if (statusFilter !== "all") params.status = statusFilter;
        if (staffFilter !== "all") params.staff_id = staffFilter;
        if (categoryFilter !== "all") params.category_id = categoryFilter;
        if (startDate) params.date_from = toISODate(startDate);
        if (endDate) params.date_to = toISODate(endDate);
        if (hasNoAnswersOnly) params.has_no_answers = true;
        return params;
    };

    const loadSubmissions = async (targetPage = page) => {
        setLoading(true);
        try {
            const response = await opsAxiosInstance.get("/api/admin/checklist-submissions", {
                ...ADMIN_HEADERS,
                params: buildParams( targetPage),
            });
            if (response?.data?.success) {
                setSubmissions(response.data.data?.submissions || []);
                setPagination(
                    response.data.data?.pagination || { page: targetPage, per_page: PAGE_SIZE, total: 0, total_pages: 1 }
                );
            } else {
                toast.error(response?.data?.message || "Unable to load submission history.");
            }
        } catch (error) {
            const message = error?.response?.data?.message || "Unable to load submission history.";
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const loadOutlets = async () => {
        try {
            const response = await opsAxiosInstance.get("/api/admin/outlets", ADMIN_HEADERS);
            setOutlets(normalizeData(response?.data));
        } catch (error) {
            const message = error?.response?.data?.message || "Unable to load outlets for filtering.";
            toast.error(message);
        }
    };

    // Same endpoint ChecklistOperations.jsx uses for its Staff tab.
    const loadStaff = async () => {
        try {
            const response = await opsAxiosInstance.get("/api/admin/staff", ADMIN_HEADERS);
            setStaffList(normalizeData(response?.data));
        } catch (error) {
            const message = error?.response?.data?.message || "Unable to load staff for filtering.";
            toast.error(message);
        }
    };

    // Same endpoint ChecklistOperations.jsx uses for its Categories tab.
    const loadCategories = async () => {
        try {
            const response = await opsAxiosInstance.get("/api/admin/checklist-categories", ADMIN_HEADERS);
            setCategoriesList(normalizeData(response?.data));
        } catch (error) {
            const message = error?.response?.data?.message || "Unable to load categories for filtering.";
            toast.error(message);
        }
    };

    // Only needed for the detail modal — the list response already includes
    // outlet_name directly per submission.
    const outletNameById = useMemo(() => {
        const map = {};
        outlets.forEach((o) => {
            map[String(o?.id ?? o?._id)] = o?.outlet_name;
        });
        return map;
    }, [outlets]);

    const viewSubmission = async (id) => {
        setShowDetailModal(true);
        setDetailLoading(true);
        setDetail(null);
        try {
            const response = await opsAxiosInstance.get(`/api/admin/checklist-submissions/${id}`, ADMIN_HEADERS);
            if (response?.data?.success) {
                setDetail(response.data.data);
            } else {
                toast.error(response?.data?.message || "Unable to load this submission.");
                setShowDetailModal(false);
            }
        } catch (error) {
            const message = error?.response?.data?.message || "Unable to load this submission.";
            toast.error(message);
            setShowDetailModal(false);
        } finally {
            setDetailLoading(false);
        }
    };

    const closeDetailModal = () => {
        setShowDetailModal(false);
        setDetail(null);
    };

    // NOTE: these only reflect the currently loaded page, not the full filtered
    // result set — the API doesn't return a global status breakdown, only a
    // total record count via pagination.total.
    const submittedCountOnPage = useMemo(
        () => submissions.filter((s) => s?.status === "SUBMITTED").length,
        [submissions]
    );
    const draftCountOnPage = useMemo(
        () => submissions.filter((s) => s?.status === "DRAFT").length,
        [submissions]
    );

    const summaryCards = [
        { title: "Total Records", value: pagination.total ?? 0, icon: "ri-file-list-3-line", color: "#2563eb", tint: "#eff6ff" },
        { title: "Submitted (page)", value: submittedCountOnPage, icon: "ri-checkbox-circle-line", color: "#059669", tint: "#ecfdf5" },
        { title: "Drafts (page)", value: draftCountOnPage, icon: "ri-draft-line", color: "#d97706", tint: "#fffbeb" },
    ];

    const totalPages = pagination.total_pages || 1;
    const safePage = pagination.page || page;

    // ---- react-select option lists ----
    const outletOptions = useMemo(
        () => [
            { value: "all", label: "All outlets" },
            ...outlets.map((o) => ({ value: String(o?.id ?? o?._id), label: o?.outlet_name })),
        ],
        [outlets]
    );
    const typeOptions = [
        { value: "all", label: "All types" },
        ...CHECKLIST_TYPE_OPTIONS.map((t) => ({ value: t, label: t })),
    ];
    const frequencyOptions = [
        { value: "all", label: "All frequencies" },
        ...FREQUENCY_OPTIONS.map((f) => ({ value: f, label: f })),
    ];
    const statusOptions = [
        { value: "all", label: "All status" },
        { value: "SUBMITTED", label: "Submitted" },
        { value: "DRAFT", label: "Draft" },
    ];
    const staffOptions = useMemo(
        () => [
            { value: "all", label: "All staff" },
            ...staffList.map((s) => ({
                value: String(s?.id ?? s?._id),
                label: s?.outlet_name ? `${s?.staff_name} · ${s.outlet_name}` : s?.staff_name,
            })),
        ],
        [staffList]
    );
    const categoryOptions = useMemo(
        () => [
            { value: "all", label: "All categories" },
            ...categoriesList.map((c) => ({ value: String(c?.id ?? c?._id), label: c?.category_name })),
        ],
        [categoriesList]
    );

    const findOption = (options, value) => options.find((o) => o.value === value) || options[0];

    return (
        <React.Fragment>
            <Header />
            <style>{auStyles}</style>
            <div className="main main-app au-page">
                <div className="au-topbar">
                    <div className="au-heading">
                        <div className="au-heading-icon"><i className="ri-history-line"></i></div>
                        <div>
                            <div className="au-eyebrow">Checklist operations</div>
                            <h4 className="au-title">Audit</h4>
                            <p className="au-subtitle">History of completed and in-progress checklist submissions across outlets.</p>
                        </div>
                    </div>
                    <Button variant="outline-secondary" size="sm" onClick={() => loadSubmissions(page)} disabled={loading}>
                        <i className="ri-refresh-line me-1"></i>Refresh
                    </Button>
                </div>

                <div className="au-stats">
                    {summaryCards.map((card, idx) => (
                        <React.Fragment key={card.title}>
                            {idx > 0 && <div className="au-stat-divider" />}
                            <div className="au-stat" style={{ "--stat-color": card.color, "--stat-tint": card.tint }}>
                                <div className="au-stat-icon"><i className={card.icon}></i></div>
                                <div className="au-stat-value">{card.value}</div>
                                <div className="au-stat-label">{card.title}</div>
                            </div>
                        </React.Fragment>
                    ))}
                </div>

                <Card className="au-card">
                    <Card.Body className="bg-white">
                        <div className="au-toolbar">
                            <div className="au-toolbar-filters">
                                <div className="au-select-wrap">
                                    <Form.Label className="text-muted fw-semibold label-text">Outlet</Form.Label>
                                    <Select
                                        styles={selectStyles}
                                        options={outletOptions}
                                        value={findOption(outletOptions, outletFilter)}
                                        onChange={(opt) => setOutletFilter(opt?.value || "all")}
                                        isSearchable
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                </div>
                                <div className="au-select-wrap">
                                    <Form.Label className="text-muted fw-semibold label-text">Type</Form.Label>
                                    <Select
                                        styles={selectStyles}
                                        options={typeOptions}
                                        value={findOption(typeOptions, checklistTypeFilter)}
                                        onChange={(opt) => setChecklistTypeFilter(opt?.value || "all")}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                </div>
                                <div className="au-select-wrap">
                                    <Form.Label className="text-muted fw-semibold label-text">Frequency</Form.Label>
                                    <Select
                                        styles={selectStyles}
                                        options={frequencyOptions}
                                        value={findOption(frequencyOptions, frequencyFilter)}
                                        onChange={(opt) => setFrequencyFilter(opt?.value || "all")}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                </div>
                                <div className="au-select-wrap">
                                    <Form.Label className="text-muted fw-semibold label-text">Status</Form.Label>
                                    <Select
                                        styles={selectStyles}
                                        options={statusOptions}
                                        value={findOption(statusOptions, statusFilter)}
                                        onChange={(opt) => setStatusFilter(opt?.value || "all")}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                </div>
                                <div className="au-select-wrap au-select-wide">
                                    <Form.Label className="text-muted fw-semibold label-text">Staff Name</Form.Label>
                                    <Select
                                        styles={selectStyles}
                                        options={staffOptions}
                                        value={findOption(staffOptions, staffFilter)}
                                        onChange={(opt) => setStaffFilter(opt?.value || "all")}
                                        isSearchable
                                        placeholder="All staff"
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                </div>
                                <div className="au-select-wrap">
                                    <Form.Label className="text-muted fw-semibold label-text">Category</Form.Label>
                                    <Select
                                        styles={selectStyles}
                                        options={categoryOptions}
                                        value={findOption(categoryOptions, categoryFilter)}
                                        onChange={(opt) => setCategoryFilter(opt?.value || "all")}
                                        isSearchable
                                        placeholder="All categories"
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                </div>
                            </div>

                            <div className="au-toolbar-extra">
                                <div className="au-date-wrap">
                                    <i className="ri-calendar-line au-date-icon"></i>
                                    <DatePicker
                                        className="au-datepicker-input"
                                        selectsRange
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChange={(update) => {
                                            const [start, end] = update;
                                            setStartDate(start);
                                            setEndDate(end);
                                        }}
                                        dateFormat="dd MMM yyyy"
                                        placeholderText="Business date range"
                                        isClearable={false}
                                        popperPlacement="bottom-start"
                                    />
                                    {(startDate || endDate) && (
                                        <button
                                            type="button"
                                            className="au-date-clear"
                                            title="Clear dates"
                                            onClick={() => {
                                                setStartDate(null);
                                                setEndDate(null);
                                            }}
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                                <label className="au-switch">
                                    <input
                                        type="checkbox"
                                        checked={hasNoAnswersOnly}
                                        onChange={(e) => setHasNoAnswersOnly(e.target.checked)}
                                    />
                                    Has NO answers only
                                </label>
                            </div>
                        </div>

                        {loading ? (
                            <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>
                        ) : (
                            <>
                                <Table responsive hover size="sm" className="au-table align-middle">
                                    <thead>
                                        <tr>
                                            <th>Business date</th>
                                            <th>Outlet</th>
                                            <th>Type / Frequency</th>
                                            <th>Submitted by</th>
                                            <th>Location</th>
                                            <th>Submitted at</th>
                                            <th>Answers</th>
                                            <th>Status</th>
                                            <th className="text-end">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {submissions.length === 0 && (
                                            <tr><td colSpan={9} className="au-empty">No submissions match the current filters.</td></tr>
                                        )}
                                        {submissions.map((item) => {
                                            const outletName = item?.outlet_name || outletNameById[String(item?.outlet_id)] || `Outlet #${item?.outlet_id}`;
                                            return (
                                                <tr key={item?.id} className="au-row" onClick={() => viewSubmission(item?.id)}>
                                                    <td className="fw-medium">{formatDate(item?.business_date)}</td>
                                                    <td>{outletName}</td>
                                                    <td>
                                                        <div>{item?.checklist_type}</div>
                                                        <div className="au-subtext">{item?.frequency}</div>
                                                    </td>
                                                    <td>{item?.staff_name || "—"}</td>
                                                    <td className="au-address-cell" title={item?.address}>{item?.address || "—"}</td>
                                                    <td>{item?.status === "DRAFT" ? "—" : formatDateTime(item?.submitted_at)}</td>
                                                    <td>
                                                        <AnswerCounts yes={item?.yes_count} no={item?.no_count} total={item?.total_answers} />
                                                    </td>
                                                    <td><StatusPill status={item?.status} /></td>
                                                    <td className="text-end">
                                                        <button
                                                            type="button"
                                                            className="au-view-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                viewSubmission(item?.id);
                                                            }}
                                                        >
                                                            View details <i className="ri-arrow-right-s-line"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>

                                {pagination.total > PAGE_SIZE && (
                                    <div className="au-pagination">
                                        <span className="au-pagination-info">
                                            Showing {(safePage - 1) * PAGE_SIZE + 1}
                                            –{Math.min(safePage * PAGE_SIZE, pagination.total)} of {pagination.total} submissions
                                        </span>
                                        <div className="au-pagination-controls">
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                disabled={safePage <= 1}
                                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                            >
                                                Prev
                                            </Button>
                                            <span className="au-pagination-page">Page {safePage} of {totalPages}</span>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                disabled={safePage >= totalPages}
                                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                            >
                                                Next
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </Card.Body>
                </Card>
            </div>

            <Modal show={showDetailModal} onHide={closeDetailModal} centered size="lg" className="au-detail-modal">
                {detailLoading ? (
                    <Modal.Body className="text-center py-5"><Spinner animation="border" size="sm" /></Modal.Body>
                ) : !detail ? (
                    <Modal.Body className="au-empty-block">Unable to load this submission.</Modal.Body>
                ) : (
                    <>
                        <Modal.Header closeButton className="au-detail-header">
                            <div className="au-detail-head-info">
                                <div className="au-detail-avatar">
                                    {(detail?.submission?.submitted_by_name || "?").trim().charAt(0).toUpperCase()}
                                </div>
                                <div>
                                    <Modal.Title className="au-modal-title">
                                        {outletNameById[String(detail?.submission?.outlet_id)] || `Outlet #${detail?.submission?.outlet_id}`}
                                    </Modal.Title>
                                    <div className="au-detail-subtitle">
                                        {detail?.submission?.checklist_type} · {detail?.submission?.frequency} · {formatDate(detail?.submission?.business_date)}
                                    </div>
                                </div>
                                <div className="au-detail-status">
                                    <StatusPill status={detail?.submission?.status} />
                                </div>
                            </div>
                        </Modal.Header>
                        <Modal.Body>
                            <div className="au-summary-strip">
                                <div className="au-summary-pill">
                                    <div className="au-summary-value">{detail?.summary?.total_answers ?? 0}</div>
                                    <div className="au-summary-label">Total answers</div>
                                </div>
                                <div className="au-summary-pill is-yes">
                                    <div className="au-summary-value">{detail?.summary?.yes_count ?? 0}</div>
                                    <div className="au-summary-label">Yes</div>
                                </div>
                                <div className="au-summary-pill is-no">
                                    <div className="au-summary-value">{detail?.summary?.no_count ?? 0}</div>
                                    <div className="au-summary-label">No</div>
                                </div>
                            </div>

                            <Row className="au-detail-meta">
                                <Col md={6}>
                                    <div className="au-meta-row">
                                        <i className="ri-user-3-line au-meta-icon"></i>
                                        <div>
                                            <div className="au-meta-label">Submitted by</div>
                                            <div className="au-meta-value">{detail?.submission?.submitted_by_name || "—"}</div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="au-meta-row">
                                        <i className="ri-time-line au-meta-icon"></i>
                                        <div>
                                            <div className="au-meta-label">Submitted at</div>
                                            <div className="au-meta-value">
                                                {detail?.submission?.status === "DRAFT" ? "Not yet submitted" : formatDateTime(detail?.submission?.submitted_at)}
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={12}>
                                    <div className="au-meta-row">
                                        <i className="ri-map-pin-line au-meta-icon"></i>
                                        <div>
                                            <div className="au-meta-label">Location</div>
                                            <div className="au-meta-value">
                                                {detail?.submission?.address || "—"}
                                                {detail?.submission?.latitude && detail?.submission?.longitude && (
                                                    <a
                                                        className="au-map-link"
                                                        href={`https://www.google.com/maps?q=${detail.submission.latitude},${detail.submission.longitude}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        View on map <i className="ri-external-link-line"></i>
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            <hr className="my-3" />

                            {(!detail?.categories || detail.categories.length === 0) ? (
                                <div className="au-empty-block">No task answers recorded for this submission.</div>
                            ) : (
                                detail.categories.map((group) => (
                                    <div key={group.category_id} className="au-answer-group">
                                        <div className="au-answer-group-head">
                                            <span className="au-answer-group-title">{group.category_name}</span>
                                            <span className="au-group-badges">
                                                <span className="au-group-badge is-yes">{group.yes_count ?? 0} Yes</span>
                                                <span className="au-group-badge is-no">{group.no_count ?? 0} No</span>
                                            </span>
                                        </div>
                                        <div className="au-answer-list">
                                            {group.answers.map((a) => (
                                                <div key={a?.task_id} className="au-answer-row">
                                                    <div className="au-answer-task">
                                                        <div>{a?.task_name}</div>
                                                        {a?.remark && <div className="au-answer-remark">{a.remark}</div>}
                                                    </div>
                                                    <AnswerBadge answer={a?.answer} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="outline-secondary" size="sm" onClick={closeDetailModal}>Close</Button>
                        </Modal.Footer>
                    </>
                )}
            </Modal>
        </React.Fragment>
    );
};

const auStyles = `
  .au-page { padding: 24px 28px 40px; }
  .au-eyebrow { font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; color: #4338ca; font-weight: 700; margin-bottom: 3px; }
  .au-title { font-weight: 700; color: #14161a; margin-bottom: 3px; font-size: 21px; letter-spacing: -0.3px; }
  .au-subtitle { color: #6b7280; font-size: 13.5px; margin: 0; }
  .au-topbar { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 18px; padding-bottom: 18px; border-bottom: 1px solid #e5e7eb; flex-wrap: wrap; }
  .au-heading { display: flex; align-items: flex-start; gap: 14px; }
  .au-heading-icon {
    width: 42px; height: 42px; flex-shrink: 0; border-radius: 6px;
    background: #eef2ff; color: #4338ca;
    display: flex; align-items: center; justify-content: center; font-size: 19px;
    border: 1px solid #e0e4fb;
  }

  .au-stats { display: flex; align-items: stretch; border: 1px solid #e5e7eb; background: #ffffff; margin-bottom: 20px; overflow: hidden; }
  .au-stat { position: relative; flex: 1; padding: 16px 20px 14px; background: #ffffff; transition: background-color 0.15s ease; }
  .au-stat::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: var(--stat-color, #1a1a1a); }
  .au-stat:hover { background: #fafafa; }
  .au-stat-icon {
    width: 26px; height: 26px; border-radius: 5px; margin-bottom: 10px;
    display: flex; align-items: center; justify-content: center;
    background: var(--stat-tint, #f3f4f6); color: var(--stat-color, #4b5563);
    font-size: 13px;
  }
  .au-stat-value { font-size: 24px; font-weight: 700; color: #1a1a1a; line-height: 1.2; letter-spacing: -0.5px; }
  .au-stat-label { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.8px; margin-top: 3px; font-weight: 600; }
  .au-stat-divider { width: 1px; background: #e5e7eb; }

  .au-card { border: 1px solid #e5e7eb; border-radius: 4px; box-shadow: none; }

  /* ── Filter toolbar ──
     Row 1 (.au-toolbar-filters): all dropdown filters, evenly spaced, wraps cleanly.
     Row 2 (.au-toolbar-extra): date range + checkbox, aligned left under the filters. */
  .au-toolbar { margin-bottom: 16px; }

  .au-toolbar-filters {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 14px 18px;
    margin-bottom: 14px;
  }

  .au-toolbar-extra {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
    padding-top: 12px;
    border-top: 1px dashed #e8eaef;
  }

  .au-select-wrap { width: 150px; }
  .label-text { font-size: 12px }
  .au-select-wide { width: 190px; }

  .au-date-wrap { position: relative; display: flex; align-items: center; }
  .au-date-icon { position: absolute; left: 9px; font-size: 13px; color: #8a8f98; pointer-events: none; z-index: 1; }
  .au-datepicker-input {
    width: 210px; height: 31px; font-size: 12.5px; padding: 0 24px 0 28px;
    border: 1px solid #d5d8dd; border-radius: 6px; color: #212529; background: #fff;
  }
  .au-datepicker-input:focus { border-color: #86b7fe; box-shadow: 0 0 0 0.2rem rgba(13,110,253,.15); outline: none; }
  .react-datepicker-popper { z-index: 1060; }
  .au-date-clear {
    position: absolute; right: 6px; background: none; border: none; color: #9ca3af;
    font-size: 15px; line-height: 1; cursor: pointer; padding: 0 2px;
  }
  .au-date-clear:hover { color: #dc2626; }

  .au-switch { display: flex; align-items: center; gap: 6px; font-size: 12.5px; color: #4b5563; white-space: nowrap; cursor: pointer; margin-left: 0; }
  .au-switch input { cursor: pointer; }

  .au-table thead th { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; color: #8a8f98; font-weight: 600; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; white-space: nowrap; }
  .au-table td { font-size: 13.5px; color: #2b2f36; border-color: #edeef0; padding-top: 10px; padding-bottom: 10px; }
  .au-row { cursor: pointer; }
  .au-row:hover { background-color: #fafbfc; }
  .au-subtext { font-size: 11.5px; color: #8a8f98; }
  .au-address-cell { max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .au-empty { text-align: center; color: #8a8f98; padding: 28px 0 !important; font-size: 13px; }
  .au-empty-block { text-align: center; color: #8a8f98; padding: 40px 0; font-size: 13px; }

  .au-status { display: inline-flex; align-items: center; gap: 7px; font-size: 12px; color: #4b5563; font-weight: 500; }
  .au-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
  .au-status.is-submitted .au-dot { background: #059669; box-shadow: 0 0 0 2px rgba(5, 150, 105, 0.15); }
  .au-status.is-draft .au-dot { background: #d97706; box-shadow: 0 0 0 2px rgba(217, 119, 6, 0.15); }

  .au-view-btn {
    display: inline-flex; align-items: center; gap: 2px;
    background: #f3f4f6; border: 1px solid #e5e7eb; border-radius: 6px;
    padding: 5px 10px; font-size: 12px; color: #1f2328; font-weight: 600; cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease, transform 0.1s ease;
  }
  .au-view-btn:hover { background: #1c1f23; border-color: #1c1f23; color: #fff; }
  .au-view-btn:active { transform: scale(0.97); }

  .au-answer-counts { display: flex; align-items: center; gap: 5px; font-size: 12px; white-space: nowrap; }
  .au-count-yes { color: #059669; font-weight: 600; }
  .au-count-no { color: #dc2626; font-weight: 600; }
  .au-count-sep { color: #d1d5db; }

  .au-pagination { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 16px; padding-top: 14px; border-top: 1px solid #edeef0; }
  .au-pagination-info { font-size: 12.5px; color: #8a8f98; }
  .au-pagination-controls { display: flex; align-items: center; gap: 10px; }
  .au-pagination-page { font-size: 12.5px; color: #4b5563; }

  /* Detail modal */
  .au-detail-header { border-bottom: 1px solid #edeef0; align-items: flex-start; }
  .au-detail-head-info { display: flex; align-items: center; gap: 12px; width: 100%; }
  .au-detail-avatar {
    width: 40px; height: 40px; border-radius: 8px; background: #1c1f23; color: #fff;
    display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 15px; flex-shrink: 0;
  }
  .au-modal-title { font-size: 16px; font-weight: 700; margin-bottom: 2px; }
  .au-detail-subtitle { font-size: 12.5px; color: #6b7280; }
  .au-detail-status { margin-left: auto; margin-right: 8px; }

  .au-summary-strip { display: flex; gap: 10px; margin-bottom: 18px; }
  .au-summary-pill { flex: 1; border: 1px solid #e8eaef; border-radius: 8px; padding: 12px 14px; text-align: center; background: #fafbfc; }
  .au-summary-pill.is-yes { background: #f0fdf6; border-color: #d3f3e3; }
  .au-summary-pill.is-no { background: #fef4f4; border-color: #f6d6d6; }
  .au-summary-value { font-size: 20px; font-weight: 700; color: #1a1a1a; line-height: 1.2; }
  .au-summary-pill.is-yes .au-summary-value { color: #059669; }
  .au-summary-pill.is-no .au-summary-value { color: #dc2626; }
  .au-summary-label { font-size: 10.5px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.6px; font-weight: 600; margin-top: 3px; }

  .au-detail-meta { row-gap: 14px; margin-bottom: 6px; }
  .au-meta-row { display: flex; align-items: flex-start; gap: 10px; }
  .au-meta-icon { font-size: 15px; color: #9ca3af; margin-top: 2px; flex-shrink: 0; }
  .au-meta-label { font-size: 10.5px; font-weight: 700; color: #8a8f98; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 2px; }
  .au-meta-value { font-size: 13.5px; color: #1f2328; }
  .au-map-link { margin-left: 10px; font-size: 12px; color: #2563eb; display: inline-flex; align-items: center; gap: 3px; }

  .au-answer-group { margin-bottom: 18px; border: 1px solid #edeef0; border-radius: 8px; overflow: hidden; }
  .au-answer-group-head { display: flex; justify-content: space-between; align-items: center; padding: 9px 12px; background: #fafbfc; border-bottom: 1px solid #edeef0; }
  .au-answer-group-title { font-size: 12px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.5px; }
  .au-group-badges { display: flex; gap: 6px; }
  .au-group-badge { font-size: 10.5px; font-weight: 700; padding: 2px 7px; border-radius: 4px; }
  .au-group-badge.is-yes { background: #ecfdf5; color: #059669; }
  .au-group-badge.is-no { background: #fef2f2; color: #dc2626; }

  .au-answer-list { background: #fff; }
  .au-answer-row { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 10px 14px; border-bottom: 1px solid #f1f2f4; font-size: 13px; }
  .au-answer-row:last-child { border-bottom: none; }
  .au-answer-task { color: #2b2f36; }
  .au-answer-remark { font-size: 12px; color: #6b7280; font-style: italic; margin-top: 3px; padding-left: 9px; border-left: 2px solid #e5e7eb; }

  .au-answer-badge { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 700; padding: 3px 9px; border-radius: 12px; white-space: nowrap; }
  .au-answer-badge.is-yes { background: #ecfdf5; color: #059669; }
  .au-answer-badge.is-no { background: #fef2f2; color: #dc2626; }
  .au-answer-badge.is-other { background: #f3f4f6; color: #6b7280; }

  /* ── Responsive ── */
  @media (max-width: 1200px) {
    .au-select-wrap, .au-select-wide { width: calc(33.333% - 12px); }
  }
  @media (max-width: 768px) {
    .au-select-wrap, .au-select-wide { width: calc(50% - 9px); }
    .au-datepicker-input { width: 100%; }
    .au-date-wrap { width: 100%; }
  }
`;

export default Audit;